import type { Tables } from "@hai/supabase";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { ApiClient } from "./api-client";
import { getSupabaseClient } from "./supabase-client";
import type {
  TimeFinderRequestDto,
  TimeFinderRequestResponse,
  TimeFinderRequestStatusDto,
} from "@hai/common";

type TimeFinderRequestRecord = Tables<"time_finder_request">;

const mapRow = (record: TimeFinderRequestRecord): TimeFinderRequestStatusDto => {
  return {
    id: record.id,
    status: record.status as TimeFinderRequestStatusDto["status"],
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    data: record.data,
  };
};

const WATCH_DEADLINE_MS = 30000;
const RETRY_DELAY_MS = 1000;
const FALLBACK_REFETCH_MS = [5000, 15000];

const isTerminal = (status: TimeFinderRequestStatusDto["status"]): boolean =>
  status === "DONE" || status === "FAILED";

export class TimeFinderService {
  constructor(private readonly api: ApiClient = new ApiClient()) {}

  async submit(body: TimeFinderRequestDto): Promise<TimeFinderRequestResponse> {
    return this.api.submitTimeFinder(body);
  }

  async submitAndWatch(
    body: TimeFinderRequestDto,
    onUpdate?: (row: TimeFinderRequestStatusDto) => void
  ): Promise<TimeFinderRequestStatusDto> {
    const { uuid } = await this.submit(body);
    return this.watch(uuid, onUpdate);
  }

  async watch(
    id: string,
    onUpdate?: (row: TimeFinderRequestStatusDto) => void
  ): Promise<TimeFinderRequestStatusDto> {
    const client = getSupabaseClient();
    const initialRow = await this.fetchRequestRow(client, id);
    if (initialRow) {
      const mapped = mapRow(initialRow);
      onUpdate?.(mapped);
      if (isTerminal(mapped.status)) {
        return mapped;
      }
    }

    return this.watchRealtime(client, id, onUpdate);
  }

  private async fetchRequestRow(
    client: ReturnType<typeof getSupabaseClient>,
    id: string
  ): Promise<TimeFinderRequestRecord | null> {
    try {
      const { data, error } = await client
        .from("time_finder_request")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) {
        console.error("[TimeFinderService] fetch error", error);
        return null;
      }
      return (data as TimeFinderRequestRecord) ?? null;
    } catch (err) {
      console.error("[TimeFinderService] fetch threw", err);
      return null;
    }
  }

  private watchRealtime(
    client: ReturnType<typeof getSupabaseClient>,
    id: string,
    onUpdate?: (row: TimeFinderRequestStatusDto) => void
  ): Promise<TimeFinderRequestStatusDto> {
    return new Promise<TimeFinderRequestStatusDto>((resolve, reject) => {
      let channel: RealtimeChannel | null = null;
      let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      const refetchTimers: Array<ReturnType<typeof setTimeout>> = [];
      let settled = false;

      const clearRefetchTimers = (): void => {
        while (refetchTimers.length) {
          const timer = refetchTimers.pop();
          if (timer) {
            clearTimeout(timer);
          }
        }
      };

      const cleanup = async (): Promise<void> => {
        if (deadlineTimer) {
          clearTimeout(deadlineTimer);
          deadlineTimer = null;
        }
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        clearRefetchTimers();
        if (channel) {
          try {
            await channel.unsubscribe();
          } catch (err) {
            console.error("[TimeFinderService] unsubscribe failed", err);
          }
          channel = null;
        }
      };

      const settleWithRow = async (row: TimeFinderRequestStatusDto): Promise<void> => {
        if (settled) {
          return;
        }
        settled = true;
        await cleanup();
        resolve(row);
      };

      const settleWithError = async (error: Error): Promise<void> => {
        if (settled) {
          return;
        }
        settled = true;
        await cleanup();
        reject(error);
      };

      const handleRow = (record: TimeFinderRequestRecord | null): void => {
        if (!record) {
          return;
        }
        const mapped = mapRow(record);
        onUpdate?.(mapped);
        if (isTerminal(mapped.status)) {
          void settleWithRow(mapped);
        }
      };

      const refetch = async (): Promise<void> => {
        if (settled) {
          return;
        }
        const data = await this.fetchRequestRow(client, id);
        handleRow(data);
      };

      const scheduleRefetches = (): void => {
        clearRefetchTimers();
        FALLBACK_REFETCH_MS.forEach((delay) => {
          refetchTimers.push(
            setTimeout(() => {
              void refetch();
            }, delay)
          );
        });
      };

      const startDeadline = (): void => {
        if (deadlineTimer) {
          clearTimeout(deadlineTimer);
        }
        deadlineTimer = setTimeout(() => {
          void settleWithError(new Error("Timed out waiting for Time Finder results"));
        }, WATCH_DEADLINE_MS);
      };

      const subscribe = (): void => {
        if (settled) {
          return;
        }
        if (channel) {
          void channel.unsubscribe();
          channel = null;
        }
        channel = client.channel(`tfr_${id}`).on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "time_finder_request",
            filter: `id=eq.${id}`,
          },
          () => {
            void refetch();
          }
        );

        channel.subscribe(async (status) => {
          if (settled) {
            return;
          }
          if (status === "SUBSCRIBED") {
            scheduleRefetches();
            await refetch();
            return;
          }
          if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
            if (!reconnectTimer) {
              reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                subscribe();
              }, RETRY_DELAY_MS);
            }
          }
        });
      };

      startDeadline();
      subscribe();
    });
  }
}
